import { Eyebrow } from '../brand';
import { HOME_SHOT, VISION_SITES, WASTE_SITES, WHY_HOME } from '../catalog';
import { useHref, useLang } from '../lang';
import { PATHS, demoHref } from '../routes';
import { Closer, SectionHead, Why } from '../sections/Shared';
import { Device } from '../sections/Software';

export function Home() {
  return (
    <>
      <BrandHero />
      <Lines />
      <Why prefix="why" ids={WHY_HOME} tone="soft" />
      <Sectors />
      <Start />
      <Hosting />
      <Closer titleKey="close.title" />
    </>
  );
}

function BrandHero() {
  const { t } = useLang();
  const href = useHref();
  return (
    <section className="brand-hero" id="top" aria-labelledby="hero-title">
      <div className="frame brand-hero-head">
        <div className="brand-hero-title">
          <Eyebrow k="home.eyebrow" />
          <h1 id="hero-title" className="display">
            {t('home.title')}
          </h1>
        </div>
        <div className="brand-hero-copy">
          <p className="hero-lead">{t('home.lead')}</p>
          <div className="actions">
            <a className="btn btn-primary" href={href(PATHS.demo)}>
              {t('home.cta')}
            </a>
            <a className="link" href="#lines">
              {t('home.lines')}
            </a>
          </div>
          <p className="hero-note">{t('home.note')}</p>
        </div>
      </div>
    </section>
  );
}

function Lines() {
  const { t } = useLang();
  const href = useHref();
  return (
    <section className="band lines" id="lines" aria-labelledby="lines-title">
      <div className="frame">
        <SectionHead id="lines-title" prefix="lines" />
        <div className="lines-grid">
          {(['waste', 'vision'] as const).map((line) => (
            <article key={line} className="line-card line-card-app" data-reveal>
              <figure className="line-card-shot">
                <Device shot={HOME_SHOT[line]} priority={line === 'waste'} />
              </figure>
              <div className="line-card-body">
                <p className="mono line-name" translate="no">
                  {t(`line.${line}`)}
                </p>
                <h3 className="h3">{t(`lines.${line}.title`)}</h3>
                <p>{t(`lines.${line}.body`)}</p>
                <ul className="ticks ticks-ink">
                  {[1, 2, 3].map((n) => (
                    <li key={n}>{t(`lines.${line}.${n}`)}</li>
                  ))}
                </ul>
                <a className="btn btn-ghost" href={href(PATHS[line])}>
                  {t(`lines.${line}.more`)}
                </a>
              </div>
            </article>
          ))}
        </div>
        <p className="lines-together" data-reveal>
          {t('lines.together')}
        </p>
      </div>
    </section>
  );
}

function Sectors() {
  const { t } = useLang();
  return (
    <section className="band sectors" aria-labelledby="where-title">
      <div className="frame">
        <SectionHead id="where-title" prefix="where" />
        <div className="where-split">
          <div data-reveal>
            <h3 className="h3">{t('where.waste')}</h3>
            <ul className="sector-grid sector-grid-list" role="list">
              {WASTE_SITES.map((s) => (
                <li key={s}>{t(`where.waste.${s}`)}</li>
              ))}
            </ul>
          </div>
          <div data-reveal>
            <h3 className="h3">{t('where.vision')}</h3>
            <ul className="sector-grid sector-grid-list" role="list">
              {VISION_SITES.map((s) => (
                <li key={s}>{t(`where.vision.${s}`)}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Start() {
  const { t } = useLang();
  const href = useHref();
  return (
    <section className="band band-soft start" id="start" aria-labelledby="start-title">
      <div className="frame">
        <SectionHead id="start-title" prefix="start" />
        <div className="start-grid">
          {(['waste', 'vision'] as const).map((line) => (
            <article key={line} data-reveal>
              <h3 className="h3">{t(`start.${line}.title`)}</h3>
              <p>{t(`start.${line}.body`)}</p>
              <dl className="spec">
                <div>
                  <dt className="mono">{t('start.k.does')}</dt>
                  <dd>{t(`start.${line}.does`)}</dd>
                </div>
                <div>
                  <dt className="mono">{t('start.k.need')}</dt>
                  <dd>{t(`start.${line}.need`)}</dd>
                </div>
                <div>
                  <dt className="mono">{t('start.k.time')}</dt>
                  <dd>{t(`start.${line}.time`)}</dd>
                </div>
              </dl>
              <a className="btn btn-primary" href={href(demoHref(line))}>
                {t(`start.${line}.cta`)}
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Hosting() {
  const { t } = useLang();
  return (
    <section className="band band-ink hosting" aria-labelledby="host-title">
      <div className="frame">
        <header className="hosting-head" data-reveal>
          <Eyebrow k="host.eyebrow" tone="paper" />
          <h2 id="host-title" className="h2">
            {t('host.title')}
          </h2>
        </header>
        <div className="host-grid">
          {(['cloud', 'own'] as const).map((k) => (
            <article key={k} data-reveal>
              <h3 className="h3">{t(`host.${k}.title`)}</h3>
              <p>{t(`host.${k}.body`)}</p>
            </article>
          ))}
        </div>
        <div className="host-sub">
          <div data-reveal>
            <h3 className="h4">{t('sec.title')}</h3>
            <ul className="ticks">
              {[1, 2, 3].map((n) => (
                <li key={n}>{t(`sec.${n}`)}</li>
              ))}
            </ul>
          </div>
          <div data-reveal>
            <h3 className="h4">{t('tools.title')}</h3>
            <p>{t('tools.body')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
